import { Locale, REDIS_KEY_SEPARATOR, RedisTtlConfig, SearchConfig } from '@headless-workspace/config';
import {
    CachedRepository,
    Result,
    ResultEmpty,
    ResultFailureFromApiError,
    ResultSuccess,
} from '@headless-workspace/core-domain';
import {
    memoryCacheAuthStrategy,
    memoryCacheQueryParamStrategy,
    memoryCacheRefreshTokenStrategy,
} from '@headless-workspace/data';
import { CachingHelpers, DecoderHelpers } from '@headless-workspace/utils';
import { VipBrandsValue } from '../config';
import { mapPopularSearch, PopularSearchDTO, PopularSearchesContainerDTO, PopularSearchValue } from './popular';
import {
    mapSearchSuggestions,
    SearchProductDTO,
    SearchSuggestionContainerDTO,
    SearchSuggestionsValue,
    TextSuggestionDTO,
} from './suggestions';

const CACHE_SERVICE_SEARCH_PREFIX = ['catalog', 'search'].join(REDIS_KEY_SEPARATOR);
const CACHE_SERVICE_SEARCH_POPULAR_KEY = 'popular';
const CACHE_SERVICE_SEARCH_SUGGESTIONS_KEY = 'suggestions';

export interface SearchRepositorySpec {
    fetchPopular(locale: Locale): Promise<Result<PopularSearchValue[]>>;

    fetchSearchSuggestions(
        locale: Locale,
        query: string | null,
        vipBrands: VipBrandsValue,
        limit?: number,
    ): Promise<Result<SearchSuggestionsValue>>;
}

const EMPTY_LIMIT = 0;

export class SearchRepository extends CachedRepository implements SearchRepositorySpec {
    path = '/catalog/v1';

    constructor() {
        super(memoryCacheAuthStrategy, memoryCacheRefreshTokenStrategy, memoryCacheQueryParamStrategy);
    }

    private async fetchRemotePopular(locale: string): Promise<Result<PopularSearchValue[]>> {
        try {
            const response = await this.datasource.getResource(`${this.path}/search`, {
                locale,
                limit: EMPTY_LIMIT,
            });

            const popularSearchesContainer = PopularSearchesContainerDTO.parse(response);
            const popularSearches = DecoderHelpers.safeParseArray(
                popularSearchesContainer.searchSuggestions.popular,
                PopularSearchDTO,
            );

            if (popularSearches.length === 0) {
                return ResultEmpty();
            }

            return ResultSuccess(popularSearches.map(mapPopularSearch));
        } catch (error) {
            const apiError = this.handleApiError(error);
            return ResultFailureFromApiError(apiError, { locale });
        }
    }

    async fetchPopular(locale: Locale): Promise<Result<PopularSearchValue[]>> {
        const params = { locale };

        return this.withCache(
            locale,
            CACHE_SERVICE_SEARCH_PREFIX,
            CachingHelpers.createCacheKey(CACHE_SERVICE_SEARCH_POPULAR_KEY, params),
            {
                ...RedisTtlConfig,
                tags: CachingHelpers.createTags(CACHE_SERVICE_SEARCH_PREFIX, CACHE_SERVICE_SEARCH_POPULAR_KEY, params),
            },
            () => this.fetchRemotePopular(locale),
        );
    }

    private async fetchRemoteSearchSuggestions(
        locale: string,
        query: string | null,
        vipBrands: VipBrandsValue,
        limit?: number,
    ): Promise<Result<SearchSuggestionsValue>> {
        if (!query) {
            return ResultEmpty();
        }

        try {
            const response = await this.datasource.getResource(`${this.path}/search`, {
                locale,
                query,
                limit: limit ?? SearchConfig.suggestionsNumberLimit,
            });

            const searchSuggestionsResponse = SearchSuggestionContainerDTO.parse(response);

            const brands = DecoderHelpers.safeParseArray(
                searchSuggestionsResponse.searchSuggestions.brands,
                TextSuggestionDTO,
            );
            const products = DecoderHelpers.safeParseArray(
                searchSuggestionsResponse.searchSuggestions.products,
                SearchProductDTO,
            );
            const categories = DecoderHelpers.safeParseArray(
                searchSuggestionsResponse.searchSuggestions.categories,
                TextSuggestionDTO,
            );
            const terms = DecoderHelpers.safeParseArray(
                searchSuggestionsResponse.searchSuggestions.terms,
                TextSuggestionDTO,
            );

            if (products.length === 0 && terms.length === 0 && brands.length === 0 && categories.length === 0) {
                return ResultEmpty();
            }

            return ResultSuccess(
                mapSearchSuggestions(
                    {
                        searchSuggestions: {
                            brands,
                            products,
                            categories,
                            terms,
                        },
                    },
                    vipBrands,
                ),
            );
        } catch (error) {
            const apiError = this.handleApiError(error);
            return ResultFailureFromApiError(apiError, { locale });
        }
    }

    async fetchSearchSuggestions(
        locale: Locale,
        query: string | null,
        vipBrands: VipBrandsValue,
        limit?: number,
    ): Promise<Result<SearchSuggestionsValue>> {
        const params = { locale, query };

        return this.withCache(
            locale,
            CACHE_SERVICE_SEARCH_PREFIX,
            CachingHelpers.createCacheKey(CACHE_SERVICE_SEARCH_SUGGESTIONS_KEY, params),
            {
                ...RedisTtlConfig,
                tags: CachingHelpers.createTags(
                    CACHE_SERVICE_SEARCH_PREFIX,
                    CACHE_SERVICE_SEARCH_SUGGESTIONS_KEY,
                    params,
                ),
            },
            () => this.fetchRemoteSearchSuggestions(locale, query, vipBrands, limit),
        );
    }
}
